import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED,
  TERMS_AND_CONDITIONS_URL
} from '@opengov/stories-shared/constants';
import { IPublication, IStory } from '@opengov/stories-shared/types';

import { getStoryStatusDisplayName } from '../../../stories/utils';
import { IExtendedStoryPrivilege, IModalMessages } from '../../../types';
import { styles } from './ShareVisibilitySettings.styles';

export interface IShareVisibilitySettingsProps {
  open: boolean;
  onClose(): void;
  entitySubdomain: string;
  hasGallery: boolean;
  isAdmin: boolean;
  userId: number;
  isTemplate: boolean;
  messages: IModalMessages;
  publication?: IPublication;
  story: IStory;
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  storiesAccessControlEnabled: boolean;
  canFollowStories: boolean;
  updateVisibility(props): Promise<void>;
  updatePublicationUpdatedAt(props: { publicationId: string; userId: number }): Promise<void>;
}

export interface IShareVisibilitySettingsState {
  currentVisibility: string;
  allowFollow: boolean;
}

export class ShareVisibilitySettings extends React.Component<
  IShareVisibilitySettingsProps,
  IShareVisibilitySettingsState
> {
  constructor(props) {
    super(props);
    const { story } = this.props;
    let currentVisibility: string;

    if (story.state === STATE_DRAFT) {
      currentVisibility = STATE_DRAFT;
    } else if (story.state === STATE_INTERNAL_PUBLISHED) {
      currentVisibility = STATE_INTERNAL_PUBLISHED;
    } else if (story.state === STATE_PUBLISHED) {
      currentVisibility = STATE_PUBLISHED;
    } else {
      currentVisibility = STATE_DRAFT; // Default fallback
    }

    this.state = {
      currentVisibility,
      allowFollow: story.isFollowable || false
    };
  }
  onVisibilitySelected = (event: SelectChangeEvent<string>) => {
    this.setState({ currentVisibility: event.target.value });
  };

  onFollowToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    this.setState({ allowFollow: isChecked });
  };

  handleSubmit = () => {
    const {
      onClose,
      entitySubdomain,
      userId,
      isAdmin,
      isTemplate,
      story,
      updateVisibility,
      updatePublicationUpdatedAt,
      publication,
      hasGallery,
      currentSubjectPublicationStoryClaim,
      storiesAccessControlEnabled
    } = this.props;
    const { currentVisibility, allowFollow } = this.state;

    const updatedStory = {
      ...story,
      isFollowable: allowFollow
    };

    void updateVisibility({
      hasGallery,
      entitySubdomain,
      userId,
      isAdmin,
      isTemplate,
      story: updatedStory,
      shareVisibility: currentVisibility,
      currentSubjectPublicationStoryClaim,
      storiesAccessControlEnabled
    }).then(() => {
      if (publication?.id) {
        void updatePublicationUpdatedAt({ publicationId: publication.id, userId });
      }
    });
    onClose();
  };

  render() {
    const { open, onClose, messages, canFollowStories } = this.props;
    const { currentVisibility, allowFollow } = this.state;

    return (
      <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
        <DialogTitle>
          {messages.changeStatus}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={styles.descriptionText}>
            {messages.changeStatusDescription}
          </Typography>
          
          <Typography id="share-visibility-label" variant="body2" sx={styles.visibilityText}>
            {messages.visibility}
          </Typography>

          <FormControl fullWidth size="small">
            <Select
              data-test="visibility-dropdown"
              name="visibility"
              value={currentVisibility}
              onChange={this.onVisibilitySelected}
              displayEmpty
              notched={false}
              renderValue={(selected) => getStoryStatusDisplayName(selected)}
              inputProps={{ 'aria-labelledby': 'share-visibility-label', className: 'no-format' }}
            >
              <MenuItem value={STATE_DRAFT} sx={styles.menuItem}>
                <Typography variant="body1">Draft</Typography>
                <Typography variant="body2" color="text.secondary">
                  {messages.onlyAccessibleByOwner}
                </Typography>
              </MenuItem>
              <MenuItem value={STATE_INTERNAL_PUBLISHED} sx={styles.menuItem}>
                <Typography variant="body1">Internal</Typography>
                <Typography variant="body2" color="text.secondary">
                  {messages.shareWithMembersOfYourOrganization}
                </Typography>
              </MenuItem>
              <MenuItem value={STATE_PUBLISHED} sx={styles.menuItem}>
                <Typography variant="body1">Public</Typography>
                <Typography variant="body2" color="text.secondary">
                  {messages.anyoneWithTheLinkCanView}
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {currentVisibility === STATE_PUBLISHED && canFollowStories && (
            <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={allowFollow}
                  onChange={this.onFollowToggle}
                  color="primary"
                  inputProps={styles.switchInputProps}
                />
              }
              label={messages.allowViewersToFollowForUpdates}
              labelPlacement="start"
              sx={styles.followFormControl}
            />
            {allowFollow ? (
            <Box sx={styles.disclaimerContainer}>
              <Box sx={styles.disclaimerBox}>
                <InfoIcon />
              <Typography  sx={styles.disclaimerText}>
                {messages.followAndCommentDesclaimer}
                <Typography  sx={{...styles.disclaimerText,...styles.termsAndConditions}}>
                <a href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer">{messages.termsAndConditions}</a>
              </Typography>
              </Typography>
              
              </Box>
            </Box>) : null} 
            
            </>
          )}
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button onClick={onClose} data-test="button-cancel" variant="text">
            {messages.cancel}
          </Button>
          <Button onClick={this.handleSubmit} data-test="button-save" variant="contained">
            {messages.change}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
